'use client'

import { useState, useEffect, useRef } from 'react'
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
  useLocation
} from 'react-router-dom'
import {
  LayoutDashboard,
  PenTool,
  BanknoteIcon,
  Sun,
  Moon,
  UsersIcon,
  X,
  Menu,
  ArrowDown,
  ArrowUp,
  FileText,
  LogIn,
  LogOut,
  Slack,
  Settings
} from 'lucide-react'
import Resumo from './Pages/resumo/resumo'
import Manutencao from './Pages/manutencao/manutencao'
import Emprestimo from './Pages/emprestimo/emprestimo'
import Login from './Pages/login/login.js'
import Users from './Pages/usuarios/UserManagement.js'
import Cadastro from './Pages/cadastro/cadastro.js'
import Relatorios from './Pages/relatorios/relatorio.js'
import LogManagement from './Pages/log/log.js'
import AdminPage from './Pages/admin/admin.js'
import ManutencaoSaida from './Pages/manutencaoSaida/manutencaoSaida.js'
import CadastroCategorias from './Pages/categorias/cadastroCategorias.js'
import Clientes from './Pages/clientes/Clientes'
import ForgotPassword from './Pages/forgotPassword/forgotPassword.js'
import RedefinirSenha from './Pages/forgotPassword/RedefinirSenha.js'

import AvisoDiarioModal from './Pages/avisoDiario/avisoDiario.js'
import ProtectedRoute from './Pages/login/protectedRoute.js'

const Dashboard = () => {
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode')
    return savedMode ? JSON.parse(savedMode) : false
  })
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [notPayed, setnotPayed] = useState(true)
  const [activeTab, setActiveTab] = useState('dashboard')
  const [showAlert, setShowAlert] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const sidebarRef = useRef(null)

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode))
    if (darkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [darkMode])

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        sidebarOpen
      ) {
        setSidebarOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [sidebarOpen])

  const handleLogout = () => {
    localStorage.setItem('isAuthenticated', 'false')
    localStorage.removeItem('idUsuario')
    navigate('/login')
  }

  const isLoginPage =
    location.pathname === '/login' ||
    location.pathname === '/cadastro' ||
    location.pathname === '/recuperacao' ||
    location.pathname === '/redefinir-senha'

  const idUsuario = localStorage.getItem('idUsuario')

  useEffect(() => {
    const verificarUsuario = async () => {
      try {
        const response = await fetch('https://sismoney.com/painel/usuarios.php')
        const usuarios = await response.json()
        const usuarioAtual = usuarios.find(
          user => user.idUsuario === Number(idUsuario)
        )
        if (usuarioAtual && usuarioAtual.ativo === 0) {
          const createdAt = new Date(usuarioAtual.created_at)
          const hoje = new Date()
          const diffDias = Math.floor(
            (hoje - createdAt) / (1000 * 60 * 60 * 24)
          )
          if (diffDias > 5) {
            setShowAlert(true)
            localStorage.removeItem('isAuthenticated')
            localStorage.removeItem('idUsuario')
            navigate('/login')
          }
        }
      } catch (error) {
        console.error('Erro ao verificar usuário:', error)
      }
    }

    verificarUsuario()
  }, [idUsuario])

  if (isLoginPage) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/cadastro" element={<Cadastro />} />
        <Route path="/recuperacao" element={<ForgotPassword />} />
        <Route path="/redefinir-senha" element={<RedefinirSenha />} />
        <Route
          path="/redefinir-senha?token=:token"
          element={<RedefinirSenha />}
        />
      </Routes>
    )
  }

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen)

  const toggleDarkMode = () => setDarkMode(!darkMode)

  return (
    <div className={darkMode ? 'dark' : ''}>
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        {showAlert && (
          <div className="alert bg-red-500 text-white p-4 rounded-md fixed top-10 left-1/2 transform -translate-x-1/2 z-50">
            <p>
              Seu acesso expirou! Efetue o pagamento para continuar usando o
              sistema.
            </p>
          </div>
        )}

        <AvisoDiarioModal />
        {/* Sidebar */}
        <div
          ref={sidebarRef}
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } fixed inset-y-0 left-0 z-30 w-64 bg-white dark:bg-gray-800 shadow-lg transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}
        >
          <div className="flex flex-col h-full">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  Dashboard
                </h1>
                <button
                  onClick={toggleDarkMode}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  {darkMode ? (
                    <Sun className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-500" />
                  )}
                </button>
              </div>
            </div>

            <nav className="flex-1 p-4">
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => {
                      setActiveTab('dashboard')
                      navigate('/')
                      setSidebarOpen(false)
                    }}
                    className={`w-full flex items-center p-3 rounded-lg transition-colors
                      ${
                        activeTab === 'dashboard'
                          ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                      }`}
                  >
                    <Menu className="w-5 h-5 mr-3" />
                    Menu
                  </button>
                </li>

                <li>
                  <button
                    onClick={() => {
                      setActiveTab('clients')
                      navigate('/clientes')
                      setSidebarOpen(false)
                    }}
                    className={`w-full flex items-center p-3 rounded-lg transition-colors
                      ${
                        activeTab === 'clients'
                          ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                      }`}
                  >
                    <UsersIcon className="w-5 h-5 mr-3" />
                    Clientes
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      setActiveTab('maintenance')
                      navigate('/entrada')
                      setSidebarOpen(false)
                    }}
                    className={`w-full flex items-center p-3 rounded-lg transition-colors
                      ${
                        activeTab === 'maintenance'
                          ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                      }`}
                  >
                    <ArrowDown className="w-5 h-5 mr-3" />
                    Contas a Receber
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      setActiveTab('maintenanceSaida')
                      navigate('/saida')
                      setSidebarOpen(false)
                    }}
                    className={`w-full flex items-center p-3 rounded-lg transition-colors
                      ${
                        activeTab === 'maintenanceSaida'
                          ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                      }`}
                  >
                    <ArrowUp className="w-5 h-5 mr-3" />
                    Contas a Pagar
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      setActiveTab('loans')
                      navigate('/emprestimo')
                      setSidebarOpen(false)
                    }}
                    className={`w-full flex items-center p-3 rounded-lg transition-colors
                      ${
                        activeTab === 'loans'
                          ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                      }`}
                  >
                    <LogIn className="w-5 h-5 mr-3" />
                    Empréstimo
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      setActiveTab('clients')
                      navigate('/clientes')
                      setSidebarOpen(false)
                    }}
                    className={`w-full flex items-center p-3 rounded-lg transition-colors
                      ${
                        activeTab === 'clients'
                          ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                      }`}
                  >
                    <UsersIcon className="w-5 h-5 mr-3" />
                    Clientes
                  </button>
                </li>

                <li>
                  <button
                    onClick={() => {
                      setActiveTab('categoria')
                      navigate('/categoria')
                      setSidebarOpen(false)
                    }}
                    className={`w-full flex items-center p-3 rounded-lg transition-colors
                      ${
                        activeTab === 'categoria'
                          ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                      }`}
                  >
                    <Slack className="w-5 h-5 mr-3" />
                    Categorias
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      setActiveTab('logs')
                      navigate('/logs')
                      setSidebarOpen(false)
                    }}
                    className={`w-full flex items-center p-3 rounded-lg transition-colors
                      ${
                        activeTab === 'logs'
                          ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                      }`}
                  >
                    <FileText className="w-5 h-5 mr-3" />
                    Logs
                  </button>
                </li>

                <li>
                  <button
                    onClick={() => {
                      setActiveTab('relatorios')
                      navigate('/relatorios')
                      setSidebarOpen(false)
                    }}
                    className={`w-full flex items-center p-3 rounded-lg transition-colors
                      ${
                        activeTab === 'relatorios'
                          ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                      }`}
                  >
                    <FileText className="w-5 h-5 mr-3" />
                    Relatorios
                  </button>
                </li>
                {idUsuario === '8' && (
                  <li>
                    <button
                      onClick={() => {
                        setActiveTab('users')
                        navigate('/users')
                        setSidebarOpen(false)
                      }}
                      className={`w-full flex items-center p-3 rounded-lg transition-colors
                        ${
                          activeTab === 'users'
                            ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                        }`}
                    >
                      <UsersIcon className="w-5 h-5 mr-3" />
                      Usuários
                    </button>
                  </li>
                )}
                {idUsuario === '8' && (
                  <li>
                    <button
                      onClick={() => {
                        setActiveTab('admin')
                        navigate('/admin')
                        setSidebarOpen(false)
                      }}
                      className={`w-full flex items-center p-3 rounded-lg transition-colors
                        ${
                          activeTab === 'admin'
                            ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                        }`}
                    >
                      <Settings className="w-5 h-5 mr-3" />
                      Admin
                    </button>
                  </li>
                )}
              </ul>
            </nav>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleLogout}
                className="w-full flex items-center p-3 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/50 transition-colors"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <header className="bg-white dark:bg-gray-800 shadow-md py-4 px-4 flex items-center justify-between lg:hidden">
            <button
              onClick={toggleSidebar}
              className="text-gray-500 hover:text-gray-600 focus:outline-none focus:text-gray-600"
            >
              {sidebarOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
            <h1 className="text-xl font-semibold text-gray-800 dark:text-white">
              Dashboard
            </h1>
            <div className="w-6"></div> {/* Spacer for centering */}
          </header>
          <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
            <div className="container mx-auto px-6 py-8">
              <Routes>
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Resumo />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/entrada"
                  element={
                    <ProtectedRoute>
                      <Manutencao />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/saida"
                  element={
                    <ProtectedRoute>
                      <ManutencaoSaida />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/emprestimo"
                  element={
                    <ProtectedRoute>
                      <Emprestimo />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/clientes"
                  element={
                    <ProtectedRoute>
                      <Clientes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute>
                      <Users />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/logs"
                  element={
                    <ProtectedRoute>
                      <LogManagement />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/relatorios"
                  element={
                    <ProtectedRoute>
                      <Relatorios />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/categoria"
                  element={
                    <ProtectedRoute>
                      <CadastroCategorias />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </div>
          </main>
        </div>

        {/* WhatsApp Button */}
        <div className="fixed bottom-4 right-4 z-50">
          <a
            href="https://wa.me/message/5AKQ43IE7UE5L1"
            className="flex items-center justify-center bg-green-500 text-white p-3 rounded-full shadow-lg hover:bg-green-600 transition duration-300"
          >
            <svg
              className="w-6 h-6"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M21 11.5c0 1.4-.6 2.6-1.6 3.4l-4.8-5.7 4.8-5.7c1.1.8 1.6 2 1.6 3.4zM12 9c-.3 0-.6-.1-.9-.2l-1.8-.9-1.4 1.4 1.4 1.8c-.1.3-.2.6-.2.9 0 1.3 1 2.3 2.3 2.3s2.3-1 2.3-2.3c0-1.2-.9-2.3-2.3-2.3zm4.9 5.7l1.3 1.5c-1.7 2.2-4.4 3.7-7.3 3.7-4.3 0-7.8-2.9-9.2-6.9 1.4-.3 2.7-.7 4.1-1.1l1.5 1.4c1.2 1.7 3.1 2.8 5.4 2.8 1.5 0 2.9-.5 4-1.3zM3.4 16.4l1.4-1.8c1-.4 2.1-.7 3.2-.9-1-2.4-1.6-5-1.6-7.7 0-.7.1-1.4.1-2.1-.7-.1-1.4-.1-2.1-.1-1.1 0-2.2.3-3.2.9l1.4 1.8c.7-.3 1.4-.5 2.1-.7-.1.7-.2 1.4-.2 2.1 0 2.8.6 5.4 1.6 7.7z" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  )
}

const App = () => (
  <Router>
    <Dashboard />
  </Router>
)

export default App
